import { capitalize, Divider, Popover, Stack, Typography } from '@mui/material';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { OPERATORS } from '@/components/conditions/OperatorSelect';
import {
  ConditionPanelTriggerButton,
  MultiSelectConditionPanel,
} from '@/components/conditions/conditionComponents';
import { SelectedItemsBody } from '@/components/panels/selectionPanel/selectedItemsBody';
import { SelectedItemsHeader } from '@/components/panels/selectionPanel/selectedItemsHeader';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import { SearchBar } from '@/components/search/searchBar';
import {
  isValueCondition,
  type MultiSelectConditionComponentProps,
} from '@/types/advanceFilter';
import { useDebouncedValue } from '@/utils/useDebouncedValue';

import { useResourceIdentifiers } from '../useResourceIentifiers';

export const ResourceNameCondition = (
  props: MultiSelectConditionComponentProps
) => {
  if (
    props.condition.operator === ConditionOperatorEnum.In ||
    props.condition.operator === ConditionOperatorEnum.NotIn
  ) {
    return <ResourceNameSelection {...props} />;
  }

  return <ResourceNameManualSelection entityName='resource name' {...props} />;
};

const ResourceNameSelection = (props: MultiSelectConditionComponentProps) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 300);

  const { data: body, isLoading } = useResourceIdentifiers(
    props.supportedProviders,
    props.supportedResourceTypes,
    debouncedSearch || undefined
  );

  const names = useMemo(
    () =>
      Array.from(new Set((body?.resources || []).map((x) => x.name)))
        .sort()
        .map((x) => ({ id: x, name: x })),
    [body]
  );

  return (
    <MultiSelectConditionPanel
      showAddNewButton
      showSearchBox
      isLoading={isLoading}
      entityName='name'
      options={names}
      onSearchChange={setSearchText}
      {...props}
      onClose={() => {
        setSearchText('');
        props.onClose?.();
      }}
    />
  );
};

const ResourceNameManualSelection = (
  props: MultiSelectConditionComponentProps
) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(props.popoverOpen || false);
  const [valueFilter, setValueFilter] = useState('');

  useEffect(() => {
    if (props.popoverOpen !== undefined) {
      setPopoverOpen(props.popoverOpen);
    }
  }, [props.popoverOpen]);

  const selectedValues =
    (isValueCondition(props.condition) && props.condition?.value) || [];

  const options = selectedValues.map((value) => ({ id: value, name: value }));

  return (
    <>
      <ConditionPanelTriggerButton
        ref={setAnchorEl}
        onClick={() => setPopoverOpen(true)}
        useImagesInSelectedView={props.useImagesInSelectedView}
        shouldShowId={props.shouldShowId}
        options={options}
        selectedValues={selectedValues}
      />
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => {
          setPopoverOpen(false);
          props.onClose?.();
        }}
      >
        <Stack className='w-[308px] max-h-[480px] overflow-hidden'>
          <Stack className='px-[24px] py-[12px]' sx={{}}>
            <Typography className='my-[12px]' variant='body1'>
              {`Add one or more ${props.entityName}s`}
            </Typography>
            <SearchBar
              hideSearchIcon
              value={valueFilter}
              setValue={setValueFilter}
              placeholder={capitalize(
                `${
                  props.property?.policyRelation &&
                  OPERATORS[props.property.policyRelation]?.[
                    props.condition.operator as ConditionOperatorEnum
                  ]?.label
                }...`
              )}
              selectedValues={selectedValues}
              onAddClick={(value) => {
                props.onChange({ ...props.condition, value });
              }}
              showAddButton={true}
            />
          </Stack>
          {selectedValues.length > 0 && (
            <>
              <Divider />
              <Stack
                sx={{
                  overflow: 'hidden',
                  flexGrow: 1,
                }}
              >
                <ScrollableWrapper>
                  <SelectedValuesContent
                    selectedValues={selectedValues}
                    onClear={() =>
                      props.onChange({ ...props.condition, value: [] })
                    }
                    onRemove={(item) => {
                      props.onChange({
                        ...props.condition,
                        value: selectedValues.filter((i) => i !== item),
                      });
                    }}
                  />
                </ScrollableWrapper>
              </Stack>
            </>
          )}
        </Stack>
      </Popover>
    </>
  );
};

const SelectedValuesContent = ({
  selectedValues,
  onClear,
  onRemove,
}: {
  selectedValues: string[];
  onClear: () => void;
  onRemove: (item: string) => void;
}) => {
  const { setIsScrolledVertically } = useScrollable();

  return (
    <>
      <ScrollableHeader
        hideBorderWhenOnTop
        className='py-[12px] px-[24px]'
        sx={{ '& .MuiTypography-root': { fontWeight: 'bold' } }}
      >
        <SelectedItemsHeader
          title={`${selectedValues.length} value${selectedValues.length === 1 ? '' : 's'} added`}
          clearAll={onClear}
        />
      </ScrollableHeader>
      <Divider />
      <SelectedItemsBody
        items={selectedValues}
        renderItem={(item) => item}
        itemKey={(item) => item}
        onRemove={onRemove}
        onScrollVerticallyChange={setIsScrolledVertically}
      />
    </>
  );
};
